import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom' // Add this import
import { connect } from 'react-redux'
import { Button } from 'reactstrap'
import branditRails from '../../../api/brandit_rails'

// Initialize Stripe with publishable key from props
let stripePromise = null
const initializeStripe = publishableKey => {
  if (!stripePromise && publishableKey) {
    stripePromise = loadStripe(publishableKey)
  }
  return stripePromise
}

// Modal checkout form component
const ModalCheckoutForm = ({
  youPay,
  currentUser,
  setPaymentMethod,
  onCardDataChange,
  onClose,
  clientSecret,
  orderData,
  paymentIntentResponse,
  setOrderCreated,
  setOrderDetails,
  onPaymentSuccess,
  checkout
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState(null)
  const [message, setMessage] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const createOrderWithPaymentConfirmation = async (orderData, paymentIntentResponse) => {
    //Check if paymentIntentResponse exists
    if (!paymentIntentResponse) {
      throw new Error('Payment intent response not available')
    }

    // Check if orderData exists
    if (!orderData) {
      throw new Error('Order data not available')
    }

    const orderFormData = new FormData()
    orderFormData.append('invoice_number', paymentIntentResponse.invoice_number)
    orderFormData.append('payment_intent_id', paymentIntentResponse.payment_intent_id)
    orderFormData.append('description', 'Stripe Payment Authorization')

    Object.keys(orderData).forEach(key => {
      if (orderData[key] !== null && orderData[key] !== undefined) {
        if (typeof orderData[key] === 'object') {
          orderFormData.append(key, JSON.stringify(orderData[key]))
        } else {
          orderFormData.append(key, orderData[key])
        }
      }
    })

    const orderResponse = await branditRails.post('/orders/create', orderFormData)
    const orderResult = orderResponse.data

    if (orderResult.status === 'success') {
      setOrderDetails(orderResult)
      setOrderCreated(true)
      return { success: true, order: orderResult }
    } else {
      setMessage('Order creation failed. Please contact support.')
      throw new Error(orderResult.error || 'Order creation failed')
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      const cardElement = elements.getElement(CardElement)

      if (!cardElement) {
        setError('Card element not found. Please refresh and try again.')
        setIsProcessing(false)
        return
      }

      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name:
              orderData?.billing_address?.name ||
              orderData?.billing_address?.full_name ||
              currentUser?.full_name ||
              `${currentUser?.first_name} ${currentUser?.last_name}`,
            email: currentUser?.email || checkout?.contactEmail || 'customer@example.com',
            phone:
              orderData?.billing_address?.phone ||
              orderData?.billing_address?.phone_number ||
              orderData?.billing_address?.address_phone ||
              currentUser?.phone ||
              '000-000-0000',
            address: {
              country: orderData?.billing_address?.country_code || orderData?.billing_address?.country || 'US',
              line1: (() => {
                const companyName = orderData?.billing_address?.company_name
                const addressLine1 =
                  orderData?.billing_address?.address_line_1 || orderData?.billing_address?.address1 || 'N/A'
                return companyName && companyName !== 'N/A' ? `${companyName} - ${addressLine1}` : addressLine1
              })(),
              line2: orderData?.billing_address?.address_line_2 || orderData?.billing_address?.address2 || null,
              city: orderData?.billing_address?.city || 'N/A',
              state: orderData?.billing_address?.state || orderData?.billing_address?.region || 'N/A',
              postal_code: orderData?.billing_address?.zip_code || orderData?.billing_address?.zip || 'N/A'
            }
          }
        }
      })

      if (paymentError) {
        setError(paymentError.message)
      } else if (paymentIntent.status === 'succeeded') {
        // Update payment method
        if (setPaymentMethod) {
          setPaymentMethod('stripe_elements')
        }

        // NEW: Create the order with payment confirmation
        try {
          const orderResult = await createOrderWithPaymentConfirmation(orderData, paymentIntentResponse)

          // Check if order creation succeeded
          if (orderResult && orderResult.success === true) {
            if (onPaymentSuccess) {
              onPaymentSuccess(orderResult)
            }
          }

          setMessage('Payment succeeded and order created! Closing form...')
        } catch (error) {
          setError('Payment succeeded but order creation failed. Please contact support.')
          return
        }

        // Close modal after success
        setTimeout(() => {
          onClose()
        }, 1500)
      } else if (paymentIntent.status === 'requires_capture') {
        // Update payment method
        if (setPaymentMethod) {
          setPaymentMethod('stripe_elements')
        }

        // NEW: Create the order with payment authorization
        try {
          const orderResult = await createOrderWithPaymentConfirmation(orderData, paymentIntentResponse)

          // Call success callback with order data
          if (orderResult && orderResult.success === true) {
            if (onPaymentSuccess) {
              onPaymentSuccess(orderResult)
            }
          }

          setMessage('Payment authorized and order created! Closing form...')
        } catch (error) {
          setError('Payment authorized but order creation failed. Please contact support.')
          return
        }

        // Close modal after success
        setTimeout(() => {
          onClose()
        }, 1500)
      } else if (paymentIntent.status === 'processing') {
        setMessage('Your payment is processing.')
      } else if (paymentIntent.status === 'requires_payment_method') {
        setMessage('Your payment was not successful, please try again.')
      } else {
        setMessage('Something went wrong.')
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <CardElement />
      {error && <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{error}</div>}
      {message && <div style={{ color: 'green', marginTop: '10px', fontSize: '14px' }}>{message}</div>}

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: 'pointer',
            flex: 1
          }}
        >
          {isProcessing ? 'Processing...' : `Pay $${(youPay / 100).toFixed(2)}`}
        </button>

        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            cursor: 'pointer',
            flex: 1
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// Main component that handles the modal and Stripe initialization
const StripeCheckoutUIModal = ({
  youPay,
  currentUser,
  setPaymentMethod,
  onCardDataChange,
  renderFormOnly,
  paymentGatewayConfig,
  orderData,
  onPaymentSuccess,
  onPaymentError,
  hideModal,
  checkout,
  checkoutSessionToken
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [wasCancelled, setWasCancelled] = useState(false)
  const [paymentIntentResponse, setPaymentIntentResponse] = useState(null)
  const [orderCreated, setOrderCreated] = useState(false)
  const [orderDetails, setOrderDetails] = useState(null)

  // Auto-trigger modal when renderFormOnly is true
  useEffect(() => {
    if (renderFormOnly && !isModalOpen && !isLoading && !wasCancelled) {
      openModal()
    }
  }, [renderFormOnly, isModalOpen, wasCancelled]) // Add wasCancelled to dependencies

  const openModal = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Helper function to format address data with N/A fallbacks (same as Authorize.net)
      const formatAddress = address => {
        if (!address) {
          return {
            name: 'N/A',
            company_name: 'N/A',
            address_line_1: 'N/A',
            address_line_2: 'N/A',
            city: 'N/A',
            state: 'N/A',
            zip_code: 'N/A',
            country: 'N/A',
            country_code: 'N/A',
            phone: 'N/A'
          }
        }

        return {
          name: address.name || address.full_name || 'N/A',
          company_name: address.company_name || 'N/A',
          address_line_1: address.address_line_1 || address.address1 || 'N/A',
          address_line_2: address.address_line_2 || address.address2 || 'N/A',
          city: address.city || 'N/A',
          state: address.state || address.region || 'N/A',
          zip_code: address.zip_code || address.zip || 'N/A',
          country: address.country || 'N/A',
          country_code: address.country_code || 'N/A',
          phone: address.phone || address.phone_number || address.address_phone || 'N/A'
        }
      }

      const paymentData = {
        // Payment-specific data
        amount: youPay,
        currency: paymentGatewayConfig?.currency || 'usd',
        payment_method: 'stripe_elements',
        checkout_session_token: checkoutSessionToken,
        success_url: `${window.location.origin}/customer/account/dashboard`,
        cancel_url: `${window.location.origin}/check-out`,
        customer: {
          email: currentUser?.email || 'no@email.com',
          first_name: currentUser?.first_name || 'Guest',
          last_name: currentUser?.last_name || 'Checkout',
          user_id: currentUser?.id || null
        },
        // Add contact email if present (even if null)
        contact_email: orderData?.contact_email,
        // Add billing and shipping addresses (same as Authorize.net)
        billing_address: formatAddress(orderData?.billing_address),
        shipping_address: formatAddress(orderData?.customer_address),
        // Add multi-address selection flag
        multi_address_selection: orderData?.multi_address_selection
      }

      const response = await branditRails.post('/payment_gateway/process_credit_card', paymentData)

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create payment intent')
      }

      if (!response.data.client_secret) {
        throw new Error('No client_secret in response')
      }

      // NEW: Store the full payment intent response including invoice_number
      setPaymentIntentResponse(response.data)
      setClientSecret(response.data.client_secret)
      setIsModalOpen(true)
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setClientSecret('')
    setError(null)

    // ✅ Set cancelled flag to prevent auto-reopening
    setWasCancelled(true)

    if (hideModal) {
      hideModal()
    }
  }

  const handleCloseSuccess = () => {
    closeModal()
    if (onPaymentSuccess) {
      onPaymentSuccess({ success: true, order: orderDetails?.order })
    }
  }

  const createModal = () => {
    if (!isModalOpen || !clientSecret) return null

    const modalContent = (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3500
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto'
          }}
        >
          {!orderCreated ? (
            <>
              <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>Secure Payment Form</h4>

              <Elements
                stripe={initializeStripe(paymentGatewayConfig?.publishableKey)}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#007bff'
                    }
                  }
                }}
              >
                <ModalCheckoutForm
                  youPay={youPay}
                  currentUser={currentUser}
                  setPaymentMethod={setPaymentMethod}
                  onCardDataChange={onCardDataChange}
                  onClose={() => {
                    closeModal()
                    hideModal()
                  }}
                  clientSecret={clientSecret}
                  orderData={orderData}
                  paymentIntentResponse={paymentIntentResponse}
                  setOrderCreated={setOrderCreated}
                  setOrderDetails={setOrderDetails}
                  onPaymentSuccess={onPaymentSuccess}
                  checkout={checkout}
                />
              </Elements>
            </>
          ) : (
            orderCreated &&
            orderDetails && (
              <div className="stripe-success-message">
                <div className="success-icon">✅</div>
                <h3>Order Created Successfully!</h3>
                <div className="order-details">
                  <p>
                    Order Number: <strong>{orderDetails?.order?.order_number || 'N/A'}</strong>
                  </p>
                  <p>
                    Invoice Number: <strong>{orderDetails?.order?.invoice_number || 'N/A'}</strong>
                  </p>
                  <p>
                    Total: <strong>${(orderDetails?.order?.total_cents / 100).toFixed(2) || 'N/A'}</strong>
                  </p>
                  <p>
                    Status: <strong>{orderDetails?.order?.order_status || 'N/A'}</strong>
                  </p>
                  <p>
                    Created: <strong>{new Date(orderDetails?.order?.created_at).toLocaleDateString() || 'N/A'}</strong>
                  </p>
                </div>
                <button className="close-button" onClick={handleCloseSuccess}>
                  Close
                </button>
              </div>
            )
          )}
        </div>
      </div>
    )

    // Use React Portal to render outside the component tree
    return ReactDOM.createPortal(modalContent, document.body)
  }

  return (
    <>
      {/* Stripe Elements Modal Trigger */}
      <div className="stripe-modal-payment-form">
        {/* <h5 className="mb-3">Payment Information</h5> */}

        {/* Only show button when renderFormOnly is false */}
        {!renderFormOnly && (
          <div className="stripe-modal-container">
            <Button
              type="button"
              onClick={openModal}
              disabled={isLoading}
              color="primary"
              size="lg"
              style={{ width: '100%', padding: '15px' }}
            >
              {isLoading ? 'Loading...' : 'Pay with Stripe Elements'}
            </Button>
          </div>
        )}

        {/* Show loading message when renderFormOnly is true */}
        {renderFormOnly && isLoading && (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading payment form...</span>
            </div>
            <p className="mt-2">Loading secure payment form...</p>
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 bg-danger text-white rounded">
            <small>
              <strong>Error:</strong> {error}
            </small>
          </div>
        )}
      </div>

      {/* Render the modal */}
      {createModal()}
    </>
  )
}

const mapStateToProps = state => {
  return {
    youPay: state.checkout.youPay,
    currentUser: state.currentUser,
    checkout: state.checkout,
    checkoutSessionToken: state.checkoutSessionToken.token
  }
}

export default connect(mapStateToProps)(StripeCheckoutUIModal)
